import { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import OkoAniMouth from "assets/OKO_AniMouth_HigherRez.gif";
import OKOCharacter from "assets/OKO_op2_yellow.svg";
import OKOCharacterNoMouth from "assets/OKO_op2_yellow2.svg";
import OKOCharacterShadow from "assets/Shadow.svg";
import { SpeechBubble } from "components/shared/SpeechBubble/SpeechBubble";
import { motion } from "framer-motion";
import { VolumeControl } from "components/shared/VolumeControl/VolumeControl";
import { constants } from "utils";

export const ConsensusOkoAssistant = ({ phrase = "", isPlayingAudio = false, onBubbleTop }) => {
  const [showText, setShowText] = useState(false);
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  const bubbleSlotRef = useRef(null);

  useEffect(() => {
    setShowText(!!phrase);
  }, [phrase]);

  const bubbleOpen = !!phrase && showText;

  // Report the open bubble's top edge (viewport px, null when closed) so the
  // side panel can make room for the text it covers. The bubble animates its
  // size in, so keep reporting as it resizes.
  useLayoutEffect(() => {
    if (!onBubbleTop) return undefined;
    const bubble = bubbleOpen ? bubbleSlotRef.current?.firstElementChild : null;
    if (!bubble) {
      onBubbleTop(null);
      return undefined;
    }
    const report = () => onBubbleTop(bubble.getBoundingClientRect().top);
    report();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(report);
    observer?.observe(bubble);
    bubble.addEventListener("animationend", report);
    window.addEventListener("resize", report);
    return () => {
      observer?.disconnect();
      bubble.removeEventListener("animationend", report);
      window.removeEventListener("resize", report);
    };
  }, [bubbleOpen, phrase, onBubbleTop]);

  const okoCharacter = isPlayingAudio ? OKOCharacterNoMouth : OKOCharacter;

  return (
    <Container>
      <CharacterContainer>
        <WrapperWithVolume
          onMouseEnter={() => setShowVolumeControl(true)}
          onMouseLeave={() => setShowVolumeControl(false)}
        >
          <Wrapper showText={showText} onClick={() => setShowText(true)}>
            <img src={okoCharacter} style={{ width: "100%", zIndex: 10 }} alt="character" />
            <img src={OKOCharacterShadow} style={{ position: "relative", top: "-10px", width: "100%" }} alt="shadow" />
            <Mouth isPlayingAudio={isPlayingAudio} okoAniMouth={OkoAniMouth} />
          </Wrapper>
          {showVolumeControl && <VolumeControl />}
        </WrapperWithVolume>
        <BubbleSlot ref={bubbleSlotRef}>
          {phrase && showText && (
            <ColumnBubble
              text={phrase}
              hide={!showText}
              setDisplay={() => setShowText(false)}
            />
          )}
        </BubbleSlot>
      </CharacterContainer>
    </Container>
  );
};

const Mouth = styled.div`
  background-image: url(${({ isPlayingAudio, okoAniMouth }) => isPlayingAudio ? okoAniMouth : "none"});
  width: 61px;
  height: 61px;
  left: 34px;
  top: 33px;
  border-radius: 100px;
  position: absolute;
  background-size: cover;
  z-index: 10;

  @media ${constants.TABLET_MEDIA_QUERY} {
    width: 50px;
    height: 50px;
    left: 28px;
    top: 27px;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 275px;
  width: 317px;
`

const WrapperWithVolume = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* The volume control lives in its own gutter LEFT of the bot; the padding
     pushes the bot right so the gutter stays inside the column instead of
     hanging off screen like the old negative-margin version. */
  padding-left: 56px;

  /* The :not(:first-child) guard leaves the bot wrapper alone when the
     control isn't rendered. */
  & > div:last-child:not(:first-child) {
    left: 0;
    top: 50%;
    bottom: auto;
    transform: translateY(-50%);
  }
`;

const Wrapper = styled(motion.div)`
  align-items: center;
  display: grid;
  justify-content: center;
  width: 122px;
  height: 122px;

  @media ${constants.TABLET_MEDIA_QUERY} {
    width: 99px;
    height: 99px;
  }
  transform: ${({ showText }) => showText ? "scale(1)" : "scale(0.4)"};
  transition: transform 0.3s ease-in-out;
  cursor: ${({ showText }) => showText ? "default" : "pointer"};
`

// Left-aligned with the logo and the Let's Talk card at the column's 24px padding.
const CharacterContainer = styled.div`
  position: absolute;
  display: flex;
  left: 24px;
  bottom: 24px;
`;

// Lets the side panel measure the bubble; display: contents keeps it out of layout.
const BubbleSlot = styled.div`
  display: contents;
`;

// Per the tablet spec the bubble stacks ABOVE the bot, spanning the column
// from the volume gutter (56px) to the column edge, uniform radius, with the
// tail (caret) pointing down at the bot. The same form applies at every
// width so the text never relayouts as the viewport grows past the tablet
// breakpoint. 280 = the 360px column minus the bot's 24px inset and the gutter.
const ColumnBubble = styled(SpeechBubble)`
  && {
    min-width: 0;
    width: max-content;
    max-width: 280px;
    left: 56px;
    bottom: calc(100% + 14px);
    border-radius: 20px;

    &::before {
      bottom: -20px;
      left: 50px;
      transform: rotate(45deg);
    }
  }
`;
